import { indexToken } from '../gibbs/autonomousCampaign/workspaces/token';

const MASKED_COMPOUND_RE = /^COMPOUND_[A-Z]+$/;

export function atomKey(atom) {
    if (!atom || typeof atom !== 'object') return null;
    return atom.uniqueID != null ? String(atom.uniqueID) : null;
}

export function isMaskedCompoundId(value) {
    return typeof value === 'string' && MASKED_COMPOUND_RE.test(value);
}

export function bondOrder(atom, neighbor) {
    const bond = typeof atom?.bondTo === 'function' ? atom.bondTo(neighbor) : null;
    const order = bond?.orderOriginal ?? bond?.order;
    return typeof order === 'number' ? order : null;
}

function atomDegree(atom) {
    return Array.isArray(atom?.bondedAtoms)
        ? atom.bondedAtoms.filter((candidate) => candidate && candidate.elem !== 'H').length
        : null;
}

export function maskedCompoundIdForIndex(index) {
    return Number.isInteger(index) && index >= 0 ? `COMPOUND_${indexToken(index)}` : null;
}

export function maskedCompoundIdForCompound(compound, workspace) {
    const compounds = workspace?.getLoadedCompounds?.();
    if (!Array.isArray(compounds)) return null;
    const index = compounds.indexOf(compound);
    return maskedCompoundIdForIndex(index);
}

function atomLabel(atom) {
    if (!atom || typeof atom !== 'object') return null;
    const raw = atom.atom || atom.name || atom.uniqueID;
    return raw == null ? null : String(raw).slice(0, 32);
}

export function vectorDescriptor(vector) {
    if (!Array.isArray(vector) || vector.length < 2) return null;
    const [fromAtom, toAtom] = vector;
    if (!fromAtom || !toAtom) return null;
    return {
        fromElem: fromAtom.elem || null,
        toElem: toAtom.elem || null,
        order: bondOrder(fromAtom, toAtom),
        fromDegree: atomDegree(fromAtom),
        toDegree: atomDegree(toAtom),
    };
}

function descriptorsEqual(a, b) {
    return !!a && !!b
        && a.fromElem === b.fromElem
        && a.toElem === b.toElem
        && a.order === b.order
        && a.fromDegree === b.fromDegree
        && a.toDegree === b.toDegree;
}

export function candidateBondVectors(compound, maxVectors = 12) {
    const atoms = compound?.getAtoms?.();
    if (!Array.isArray(atoms) || atoms.length === 0) return [];
    const vectors = [];
    const seen = new Set();
    const add = (fromAtom, toAtom) => {
        const fromKey = atomKey(fromAtom);
        const toKey = atomKey(toAtom);
        if (!fromKey || !toKey || fromKey === toKey) return;
        const key = `${fromKey}:${toKey}`;
        if (seen.has(key)) return;
        seen.add(key);
        vectors.push([fromAtom, toAtom]);
    };

    const heavyAtoms = atoms.filter((atom) => atom && atom.elem !== 'H');
    for (const atom of heavyAtoms) {
        const bonded = Array.isArray(atom.bondedAtoms) ? atom.bondedAtoms : [];
        for (const neighbor of bonded) {
            if (!neighbor || neighbor.elem === 'H') continue;
            const order = bondOrder(atom, neighbor);
            if (order !== null && order !== 1) continue;
            add(atom, neighbor);
            add(neighbor, atom);
            if (vectors.length >= maxVectors) return vectors;
        }
    }
    return vectors;
}

export function uniqueVectorForDescriptor(compound, descriptor, maxVectors = 12) {
    if (!descriptor) return null;
    const matches = candidateBondVectors(compound, maxVectors)
        .filter((vector) => descriptorsEqual(vectorDescriptor(vector), descriptor));
    return matches.length === 1 ? matches[0] : null;
}

function isHydrogenAtom(atom) {
    return atom?.elem === 'H';
}

/** BFD grow uses vector[0] as base; never allow H as the base on an H-heavy bond. */
function orientGrowVectorAtoms(fromAtom, toAtom) {
    if (isHydrogenAtom(fromAtom) && !isHydrogenAtom(toAtom)) {
        return [toAtom, fromAtom];
    }
    return [fromAtom, toAtom];
}

function directedEvidence(fromAtom, toAtom, parentId) {
    const fromKey = atomKey(fromAtom);
    const toKey = atomKey(toAtom);
    return {
        valid: true,
        atomUniqueIds: [fromKey, toKey],
        direction: {
            fromAtomUniqueId: fromKey,
            toAtomUniqueId: toKey,
        },
        display: {
            from: atomLabel(fromAtom) || fromKey,
            to: atomLabel(toAtom) || toKey,
        },
        parentId,
        descriptor: vectorDescriptor([fromAtom, toAtom]),
    };
}

function normalizeHydrogenBaseEvidence(evidence) {
    if (
        !evidence?.valid
        || !evidence.descriptor
        || evidence.descriptor.fromElem !== 'H'
        || !evidence.descriptor.toElem
        || evidence.descriptor.toElem === 'H'
    ) {
        return evidence;
    }
    const atomUniqueIds = [evidence.atomUniqueIds[1], evidence.atomUniqueIds[0]];
    return {
        ...evidence,
        atomUniqueIds,
        direction: {
            fromAtomUniqueId: atomUniqueIds[0],
            toAtomUniqueId: atomUniqueIds[1],
        },
        display: evidence.display ? {
            from: evidence.display.to,
            to: evidence.display.from,
        } : evidence.display,
        descriptor: {
            fromElem: evidence.descriptor.toElem,
            toElem: evidence.descriptor.fromElem,
            order: evidence.descriptor.order,
            fromDegree: evidence.descriptor.toDegree,
            toDegree: evidence.descriptor.fromDegree,
        },
    };
}

export function selectedGrowVectorEvidence(selectedAtoms, workspace) {
    const atoms = Array.isArray(selectedAtoms) ? selectedAtoms.filter(Boolean) : [];
    if (atoms.length !== 2) {
        return { valid: false, reason: 'requires-two-atoms', atomCount: atoms.length };
    }
    const [selectedFrom, selectedTo] = atoms;
    const selectedFromKey = atomKey(selectedFrom);
    const selectedToKey = atomKey(selectedTo);
    if (!selectedFromKey || !selectedToKey || selectedFromKey === selectedToKey) {
        return { valid: false, reason: 'requires-two-atoms', atomCount: atoms.length };
    }
    if (isHydrogenAtom(selectedFrom) && isHydrogenAtom(selectedTo)) {
        return {
            valid: false,
            reason: 'invalid-hydrogen-vector',
            atomUniqueIds: [selectedFromKey, selectedToKey],
        };
    }

    const fromCompound = workspace?.getCompoundByAtom?.(selectedFrom) || null;
    const toCompound = workspace?.getCompoundByAtom?.(selectedTo) || null;
    if (!fromCompound || !toCompound) {
        return {
            valid: false,
            reason: 'missing-parent',
            atomUniqueIds: [selectedFromKey, selectedToKey],
        };
    }
    if (fromCompound !== toCompound) {
        return {
            valid: false,
            reason: 'mixed-parent-selection',
            atomUniqueIds: [selectedFromKey, selectedToKey],
        };
    }
    const bondedAtoms = Array.isArray(selectedFrom.bondedAtoms) ? selectedFrom.bondedAtoms : [];
    if (!bondedAtoms.includes(selectedTo)) {
        return {
            valid: false,
            reason: 'not-bonded',
            atomUniqueIds: [selectedFromKey, selectedToKey],
        };
    }
    const order = bondOrder(selectedFrom, selectedTo);
    if (order !== null && order !== 1) {
        return {
            valid: false,
            reason: 'invalid-bond-order',
            atomUniqueIds: [selectedFromKey, selectedToKey],
        };
    }
    const parentId = maskedCompoundIdForCompound(fromCompound, workspace);
    if (!parentId) {
        return {
            valid: false,
            reason: 'missing-parent',
            atomUniqueIds: [selectedFromKey, selectedToKey],
        };
    }

    const [fromAtom, toAtom] = orientGrowVectorAtoms(selectedFrom, selectedTo);
    return directedEvidence(fromAtom, toAtom, parentId);
}

export function reversedSelectedGrowVectorEvidence(evidence) {
    if (
        !evidence?.valid
        || !Array.isArray(evidence.atomUniqueIds)
        || evidence.atomUniqueIds.length !== 2
    ) {
        return evidence;
    }
    const atomUniqueIds = [evidence.atomUniqueIds[1], evidence.atomUniqueIds[0]];
    const reversed = {
        ...evidence,
        atomUniqueIds,
        direction: {
            fromAtomUniqueId: atomUniqueIds[0],
            toAtomUniqueId: atomUniqueIds[1],
        },
        display: evidence.display ? {
            from: evidence.display.to,
            to: evidence.display.from,
        } : evidence.display,
        descriptor: evidence.descriptor ? {
            fromElem: evidence.descriptor.toElem,
            toElem: evidence.descriptor.fromElem,
            order: evidence.descriptor.order,
            fromDegree: evidence.descriptor.toDegree,
            toDegree: evidence.descriptor.fromDegree,
        } : evidence.descriptor,
    };
    return normalizeHydrogenBaseEvidence(reversed);
}
